import React from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import { alpha } from '@mui/material/styles';
import PaymentLinkSendPanel from './PaymentLinkSendPanel';

/**
 * Shared footer for Booking Confirmed dialogs (walk-in, reservation, quick booking).
 */
export default function BookingConfirmedActionsContent({
  isDark,
  accent = '#4ce1b6',
  successActionLoading,
  onDownloadInvoice,
  onDownloadVoucher,
  onSendWhatsApp,
  onSendEmail,
  onDone,
  invoiceButtonLabel = 'Download invoice',
  voucherButtonLabel = 'Download voucher',
  showPaymentLink = false,
  paymentLinkBookingId = 0,
  paymentLinkBooking = null,
  paymentLinkAmount = 0,
  paymentLinkCurrency = 'INR',
  canSendPaymentLinkEmail = true,
  canSendPaymentLinkWhatsApp = true,
  onPaymentLinkSent,
}) {
  return (
    <>
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          mb: 1,
          fontWeight: 700,
          letterSpacing: 1.1,
          fontSize: '0.65rem',
          color: isDark ? '#94a3b8' : '#64748b',
        }}
      >
        Documents &amp; Payment
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, width: '100%', flexWrap: 'wrap' }}>
        <Button
          onClick={onDownloadInvoice}
          variant="contained"
          startIcon={
            successActionLoading === 'invoice' ? (
              <CircularProgress size={16} sx={{ color: '#000' }} />
            ) : (
              <DownloadIcon sx={{ fontSize: 16 }} />
            )
          }
          size="small"
          disabled={!!successActionLoading}
          sx={{
            flex: 1,
            minWidth: 110,
            backgroundColor: '#4ce1b6',
            color: '#000',
            fontWeight: 700,
            py: 0.85,
            borderRadius: 2,
            fontSize: '0.75rem',
            textTransform: 'none',
            boxShadow: '0 2px 8px rgba(76, 225, 182, 0.28)',
            '&:hover': { backgroundColor: '#3ab896' },
          }}
        >
          {invoiceButtonLabel}
        </Button>
        {typeof onDownloadVoucher === 'function' ? (
          <Button
            onClick={onDownloadVoucher}
            variant="contained"
            startIcon={
              successActionLoading === 'voucher' ? (
                <CircularProgress size={16} sx={{ color: '#000' }} />
              ) : (
                <DownloadIcon sx={{ fontSize: 16 }} />
              )
            }
            size="small"
            disabled={!!successActionLoading}
            sx={{
              flex: 1,
              minWidth: 110,
              backgroundColor: isDark ? '#334155' : '#0f766e',
              color: '#fff',
              fontWeight: 700,
              py: 0.85,
              borderRadius: 2,
              fontSize: '0.75rem',
              textTransform: 'none',
              '&:hover': { backgroundColor: isDark ? '#475569' : '#0d9488' },
            }}
          >
            {voucherButtonLabel}
          </Button>
        ) : null}
        <Button
          onClick={onSendWhatsApp}
          variant="contained"
          startIcon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
          size="small"
          disabled={!!successActionLoading}
          sx={{
            flex: 1,
            minWidth: 110,
            backgroundColor: '#25D366',
            color: '#fff',
            fontWeight: 700,
            py: 0.85,
            borderRadius: 2,
            fontSize: '0.75rem',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#20BA5A' },
          }}
        >
          Send WhatsApp
        </Button>
        <Button
          onClick={onSendEmail}
          variant="contained"
          startIcon={
            successActionLoading === 'email' ? (
              <CircularProgress size={16} sx={{ color: '#fff' }} />
            ) : (
              <EmailIcon sx={{ fontSize: 16 }} />
            )
          }
          size="small"
          disabled={!!successActionLoading}
          sx={{
            flex: 1,
            minWidth: 110,
            backgroundColor: '#1976d2',
            color: '#fff',
            fontWeight: 700,
            py: 0.85,
            borderRadius: 2,
            fontSize: '0.75rem',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#1565c0' },
          }}
        >
          Send email
        </Button>
      </Box>

      {showPaymentLink && paymentLinkBookingId > 0 ? (
        <PaymentLinkSendPanel
          layout="embedded"
          bookingId={paymentLinkBookingId}
          booking={paymentLinkBooking}
          amount={paymentLinkAmount}
          currency={paymentLinkCurrency}
          isDark={isDark}
          accent={accent}
          allowSend
          canSendEmail={canSendPaymentLinkEmail}
          canSendWhatsApp={canSendPaymentLinkWhatsApp}
          onSentSuccess={onPaymentLinkSent}
        />
      ) : null}

      <Button
        onClick={onDone}
        variant="outlined"
        size="small"
        sx={{
          mt: showPaymentLink ? 1.5 : 2,
          width: '100%',
          borderColor: isDark ? alpha('#fff', 0.14) : '#e0e0e0',
          color: isDark ? '#ddd' : '#333',
          py: 0.85,
          borderRadius: 2,
          fontSize: '0.8rem',
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': {
            borderColor: accent,
            backgroundColor: isDark ? alpha(accent, 0.08) : alpha(accent, 0.06),
          },
        }}
      >
        Done
      </Button>
    </>
  );
}
